import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectCurrentCaseId,
  selectCurrentSelectedFileMetaData,
  selectBundleTabPage,
  selectUseLegacyDownload,
} from 'common/selectors';
import history from 'common/history';
import T from 'i18n';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome } from '@fortawesome/free-solid-svg-icons';
import SearchAllFiles from 'features/viewing/AcusoftViewer/AcusoftViewerControl/SearchAllFiles';
import RecentlyPresented from 'features/viewing/AcusoftViewer/AcusoftViewerControl/RecentlyPresented';
import PresentViewClearSearchPopup from 'features/viewing/AcusoftViewer/AcusoftViewerControl/PresentViewClearSearchPopup';
import { FileDownload } from 'features/case/DocumentsModal/modals/FileDownload';
import LegacyFileDownload from 'features/case/DocumentsModal/modals/LegacyFileDownload';
import {
  safeGetJSON,
  atomicStorageOperations,
  deriveDocNavigationState,
  normalizeSlashInput,
} from 'features/viewing/AcusoftViewer/AcusoftViewerControl/hooks/usePresentView';
import api from 'common/api';

const fetchFileDetails = async (caseId, fileId) => {
  try {
    const url = `/cases/${encodeURIComponent(caseId)}/files/${encodeURIComponent(fileId)}/details?firstTrialBookOnly=true`;
    const res = await api.get(url);
    if (res?.error) return null;
    return res;
  } catch {
    return null;
  }
};

const pickBundleLocation = (doc, normalizedInput) => {
  if (!Array.isArray(doc?.bundleLocations)) return undefined;
  const parts = normalizedInput.split('/');
  const prefix = parts[0]?.toUpperCase() || '';
  const tab = parts.length > 1 ? parts[1] : undefined;
  if (!prefix) return doc.bundleLocations[0];
  return (
    doc.bundleLocations.find((bl) => {
      const gp = (bl.globalPagePrefix || '').toUpperCase();
      const tb = bl.tab != null ? String(bl.tab) : undefined;
      return gp === prefix && (tab ? tb === tab : true);
    }) || doc.bundleLocations[0]
  );
};

const buildNavigationUrl = (caseId, doc, goToPage) => {
  const folderId = doc.folderId || doc.bundleLocations?.[0]?.folderId || '';
  const globalPagePrefix = doc.globalPagePrefix || doc.bundleLocations?.[0]?.globalPagePrefix || '';
  const hasGlobalPrefix = Boolean(globalPagePrefix.trim());

  const queryParts = [
    `folderId=${folderId}`,
    `goToPage=${goToPage || 1}`,
    `pageCount=${doc.pageCount || ''}`,
  ];
  if (hasGlobalPrefix) {
    queryParts.push(
      `startPage=${doc.startPage || doc.bundleLocations?.[0]?.startPage || ''}`,
      `tab=${doc.tab || doc.bundleLocations?.[0]?.tab || ''}`,
      `globalPagePrefix=${globalPagePrefix}`,
    );
  }
  queryParts.push('view=share');

  return `/case/${caseId}/present-view/files/${doc.id}?${queryParts.join('&')}`;
};

const presentModeSearchProps = {
  documents: [],
  isAllDocsWithLongList: false,
  isClickEnterToPresent: false,
  isDocFinderPage: false,
  isPresentModePage: true,
  isPresentModeWithBackEnd: false,
  isSearchModePage: false,
  isReady: true,
  placeholder: 'Search',
  showDeepSearch: true,
  selectedColumns: ['index', 'id', 'name', 'info'],
  useOnlyIdColForPresentMode: false,
};

const PresentViewLandingPage = () => {
  const caseId = useSelector(selectCurrentCaseId);
  const currentFileMetaData = useSelector(selectCurrentSelectedFileMetaData) || {};
  const btpActive = useSelector(selectBundleTabPage);
  const useLegacyDownload = useSelector(selectUseLegacyDownload);
  const DownloadComponent = useLegacyDownload ? LegacyFileDownload : FileDownload;

  // Search state
  const [value, setValue] = useState(() => {
    try {
      const storedDocsData = safeGetJSON(sessionStorage, `${caseId}.docsToDisplay`, null);
      if (storedDocsData && !Array.isArray(storedDocsData) && storedDocsData.searchValue) {
        return storedDocsData.searchValue;
      }
      return safeGetJSON(sessionStorage, `${caseId}.searchValue`, '') || '';
    } catch {
      return '';
    }
  });
  const [docsToDisplay, setDocsToDisplay] = useState(() => {
    try {
      const storedDocsData = safeGetJSON(sessionStorage, `${caseId}.docsToDisplay`, null);
      if (storedDocsData) {
        if (Array.isArray(storedDocsData)) return storedDocsData;
        if (storedDocsData.docs && Array.isArray(storedDocsData.docs)) {
          return storedDocsData.docs.map((doc) =>
            doc?.searchContext ? doc : { ...doc, searchContext: storedDocsData.searchValue },
          );
        }
      }
    } catch {
      /* ignore */
    }
    return [];
  });
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const stored = localStorage.getItem(`${caseId}.recentSearches`);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showFileVariantsModal, setShowFileVariantsModal] = useState(false);
  const [selectedFileForDownload, setSelectedFileForDownload] = useState(null);
  const [showClearAllDocsModal, setShowClearAllDocsModal] = useState(false);
  const [chipPageNumber, setChipPageNumber] = useState(null);

  const viewerControlRef = useRef(null);
  const requestIdRef = useRef(0);

  // Navigate to present-view with the selected document
  const navigateToDoc = useCallback(
    async (doc, rawInput) => {
      const { normalizedInput, searchPage, pageNum } = deriveDocNavigationState(
        doc,
        rawInput,
        btpActive,
      );
      setChipPageNumber(searchPage);

      let effectiveDoc = doc;
      let pickedLoc = pickBundleLocation(effectiveDoc, normalizedInput);
      let bundleLoc = pickedLoc || {};

      // Fetch details if we don't have bundle info
      if (effectiveDoc.startPage == null && bundleLoc.startPage == null && effectiveDoc.id) {
        const details = await fetchFileDetails(caseId, effectiveDoc.id);
        if (details) {
          const mergedBundleLocations =
            Array.isArray(details.bundleLocations) && details.bundleLocations.length > 0
              ? details.bundleLocations
              : effectiveDoc.bundleLocations;
          effectiveDoc = { ...effectiveDoc, ...details, bundleLocations: mergedBundleLocations };
          pickedLoc = pickBundleLocation(effectiveDoc, normalizedInput);
          bundleLoc = pickedLoc || bundleLoc || {};
        }
      }

      const globalPagePrefix = (
        effectiveDoc.globalPagePrefix ||
        bundleLoc?.globalPagePrefix ||
        ''
      )?.toUpperCase();

      const docWithPage = {
        ...effectiveDoc,
        startPage: effectiveDoc.startPage ?? bundleLoc.startPage ?? '1',
        pageCount: effectiveDoc.pageCount ?? bundleLoc.pageCount,
        tab: effectiveDoc.tab ?? bundleLoc.tab ?? '1',
        globalPagePrefix,
        folderId:
          effectiveDoc.folderId ?? bundleLoc?.folderId ?? effectiveDoc.folderFileId ?? '',
        localPageNumber: pageNum,
        timestamp: Date.now(),
        displayPage: normalizedInput?.toUpperCase() || searchPage?.toUpperCase(),
        btpParameter: btpActive,
        originalInput: normalizedInput,
        searchContext: normalizedInput,
      };

      // Cache in recent searches
      if (normalizedInput.length > 0) {
        try {
          const updatedRecentSearches = await atomicStorageOperations.updateRecentSearches(
            caseId,
            (current) => {
              const next = { ...current };
              const key = `${docWithPage.id}_${normalizedInput.toUpperCase()}_${pageNum}_${globalPagePrefix}_${docWithPage.folderId}`;
              next[key] = docWithPage;
              return next;
            },
          );
          setRecentSearches(updatedRecentSearches);
        } catch (error) {
          console.error('Failed to update recent searches:', error);
        }
      }

      // Persist search state
      try {
        await atomicStorageOperations.updateCurrentlyPresenting(caseId, docWithPage);
        if (docsToDisplay.length > 0 && normalizedInput.trim()) {
          await atomicStorageOperations.persistDocsToDisplayWithContext(
            caseId,
            docsToDisplay,
            normalizedInput,
            requestIdRef.current,
          );
        }
      } catch {
        /* ignore */
      }

      const url = buildNavigationUrl(caseId, docWithPage, pageNum);
      history.push(url);
    },
    [btpActive, caseId, docsToDisplay],
  );

  const handleOpenDoc = useCallback(
    (doc, _viewerControlRef) => {
      const rawInput =
        doc?.originalInput ?? doc?.searchContext ?? value ?? '';
      navigateToDoc(doc, rawInput);
    },
    [navigateToDoc, value],
  );

  const handleOpenRecentlyPresented = useCallback(
    (doc, _viewerControlRef, _forcePresent, _origin) => {
      const rawInput = doc.originalInput || doc.searchPage || '';
      navigateToDoc(doc, rawInput);
    },
    [navigateToDoc],
  );

  const handleRemoveRecentSearch = useCallback(
    (e, searchValue) => {
      e.stopPropagation();
      const updatedSearches = { ...recentSearches };
      delete updatedSearches[searchValue];
      setRecentSearches(updatedSearches);
      localStorage.setItem(`${caseId}.recentSearches`, JSON.stringify(updatedSearches));
    },
    [recentSearches, caseId],
  );

  // Dummy startStopPresentHandler — on landing page we just navigate
  const startStopPresentHandlerWPresentView = useCallback(() => {}, []);

  const currentFileMetaDataValue = useMemo(
    () => ({ page: 1 }),
    [],
  );

  const showNoResults = docsToDisplay.length === 0 && value.length !== 0 && !isSearching;

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      {/* Left sidebar - Search & Recent */}
      <div
        style={{
          width: '320px',
          maxWidth: '400px',
          minWidth: '280px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          padding: '0.5rem 1rem',
          borderRight: '1px solid #eee',
        }}
      >
        <div style={{ marginBottom: '1rem', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FontAwesomeIcon
            icon={faHome}
            style={{ color: '#666', cursor: 'pointer', fontSize: '1.5rem' }}
            onClick={() => history.push(`/case/${caseId}`)}
            title="Back to case"
          />
          <h3 style={{ margin: 0, color: '#333', fontSize: '1.1rem' }}>
            {T.translate('case.presentViewTitle')}
          </h3>
        </div>

        <SearchAllFiles
          search={presentModeSearchProps}
          setDocsToDisplay={setDocsToDisplay}
          docsToDisplay={docsToDisplay}
          handleOpenDoc={handleOpenDoc}
          hasSearch={true}
          clickToSearch={false}
          hasDocFinderFilter={true}
          isBundleTabPage={true}
          setShowDocFinderFilter={() => {}}
          showDocFinderFilter={true}
          style={{
            display: 'flex',
            flex: 1,
            width: '100%',
            padding: '0.5rem 0',
          }}
          presentView={true}
          value={value}
          setValue={setValue}
          setSearchError={setSearchError}
          startStopPresentHandlerWPresentView={startStopPresentHandlerWPresentView}
          isSearching={isSearching}
          setIsSearching={setIsSearching}
          viewerControlRef={viewerControlRef}
          recentSearches={recentSearches}
          requestIdRef={requestIdRef}
        />

        {showNoResults && (
          <div
            style={{
              justifyContent: 'center',
              alignItems: 'center',
              padding: '1rem',
              minHeight: '100px',
              textAlign: 'center',
              color: '#666',
            }}
          >
            <p>{T.translate('case.presentView.noDocumentsFound')}</p>
            <p style={{ fontSize: '0.9rem' }}>
              {T.translate('case.presentView.noDocumentsFoundMsg')}
            </p>
            {searchError.length > 0 && (
              <div style={{ color: '#d32f2f' }}>
                <p>{searchError}</p>
              </div>
            )}
          </div>
        )}

        <RecentlyPresented
          docsToDisplay={docsToDisplay}
          handleOpenDoc={handleOpenDoc}
          handleOpenRecentlyPresented={handleOpenRecentlyPresented}
          handleRemoveRecentSearch={handleRemoveRecentSearch}
          setSelectedFileForDownload={setSelectedFileForDownload}
          setShowFileVariantsModal={setShowFileVariantsModal}
          setShowClearAllDocsModal={setShowClearAllDocsModal}
          recentSearches={recentSearches}
          currentFileMetaData={currentFileMetaData}
          currentFileMetaDataValue={currentFileMetaDataValue}
          chipPageNumber={chipPageNumber}
          viewerControlRef={viewerControlRef}
        />
      </div>

      {/* Right area - placeholder */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: '1rem',
          padding: '2rem',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>
            {T.translate('case.presentViewSelectDocument')}
          </p>
        </div>
      </div>

      {showFileVariantsModal && (
        <DownloadComponent
          show={showFileVariantsModal}
          selectedRows={[selectedFileForDownload || {}]}
          handleClose={() => setShowFileVariantsModal(false)}
        />
      )}

      <PresentViewClearSearchPopup
        open={showClearAllDocsModal}
        onClose={() => setShowClearAllDocsModal(false)}
        setRecentSearches={setRecentSearches}
        caseId={caseId}
      />
    </div>
  );
};

export default PresentViewLandingPage;
